import React from 'react';
import PageContainer from 'containers/PageContainer';
import PropTypes from 'prop-types';
import ChangeLensView from 'views/ChangeLensView';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import moment from 'moment';
import {
  findNextAndPrevPack,
  mapPack,
} from 'helpers/subscriptionHelpers';
import { mapProductOptions } from 'helpers/productOptionsHelpers';
import useDisableReasons from 'hooks/useDisableReasons';
import ChangeLensReviewView from 'views/ChangeLensReviewView';
import useMessage from 'hooks/useMessages';
import { changeUsage } from 'gateways/Api';
import { resetSubscriptionsList } from 'store/actions/subscription';
import useNavHistory from 'hooks/useNavHistory';
import { setAppBusy } from 'store/actions/system';
import changeLensAmountContainerPath from 'containers/paths/ChangeLensAmountContainerPath';

const ChangeLensAmountContainer = ({ getProductOptionsFunc }) => {
  const [plansAndSolution, setPlansAndSolution] = React.useState({ plans: [], solution: null });
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const subscription = useSelector((state) => state.subscription);
  const account = useSelector((state) => state.account);
  const store = useSelector((state) => state.store);
  const { navigateToClosestRoot } = useNavHistory();
  const today = moment().format('YYYY-MM-DD');
  const { topupsDisableReason } = useDisableReasons();

  const { planId, withSolution } = queryString.parse(location.search);
  const selectedPlan = planId && plansAndSolution.plans.find((p) => p.id === planId);

  const { nextPack, nextDeliverablePack, nextUnpaidPack } = (subscription
    && findNextAndPrevPack(subscription.packs, today)) || {};

  const canBeSkipped = nextPack && nextPack.available_actions.includes('Holiday Pack');

  const { genericMessage, genericFailureMessage } = useMessage();

  React.useEffect(() => {
    if (subscription && subscription.cart) {
      const mappedNextDeliverablePack = nextDeliverablePack
        && mapPack(nextDeliverablePack, subscription, account, store);

      getProductOptionsFunc(
        store.tax_code,
        subscription.cart.left && subscription.cart.left.lens_detail.clfit.style_number,
        subscription.cart.right && subscription.cart.right.lens_detail.clfit.style_number,
        (subscription.recommendedSolution && subscription.recommendedSolution.sku),
      )
        .then((options) => mapProductOptions(options,
          mappedNextDeliverablePack.product.packSize.quantity))
        .then(({ plans: mappedPlans, solution: mappedSolution }) => {
          setPlansAndSolution({
            plans: mappedPlans.filter(
              (o) => o.quantity <= mappedNextDeliverablePack.product.packSize.quantity,
            ),
            solution: mappedSolution && {
              ...mappedSolution,
              selected: !!(subscription.cart.extras && subscription.cart.extras.solution),
            },
          });
        });
    }
  }, [subscription, nextDeliverablePack, account, store, getProductOptionsFunc, setPlansAndSolution]);

  function onChangeLensAmount(option) {
    history.push(
      queryString.stringifyUrl({
        url: changeLensAmountContainerPath('review'),
        query: {
          planId: option.planId,
          withSolution: option.withSolution || undefined,
        },
      }),
    );
  }

  function onCancel() {
    history.goBack();
  }

  function onConfirmChange() {
    dispatch(setAppBusy(true));
    changeUsage(
      subscription.id,
      {
        taxCode: store.tax_code,
        quantity: selectedPlan.quantity,
        solution: !!withSolution,
      },
    )
      .then(() => {
        const changeDelayed = nextPack !== nextUnpaidPack;
        genericMessage(changeDelayed
          ? `All done. Your package will be updated after your ${moment(
            nextPack.delivery_date,
          ).format('MMMM YYYY')} delivery.`
          : 'All done. Your package has been updated successfully.',
        changeDelayed ? 'warning' : 'info');
      })
      .then(navigateToClosestRoot)
      .then(() => dispatch(resetSubscriptionsList()))
      .catch(() => genericFailureMessage())
      .finally(() => dispatch(setAppBusy(false)));
  }

  return (
    <PageContainer hasBack requiresSubscription requiresAccount requiresLogin>
      {!planId && (
        <ChangeLensView
          onSubmit={onChangeLensAmount}
          onCancel={onCancel}
          plans={plansAndSolution.plans}
          nextPackId={canBeSkipped ? nextPack.id : null}
          canTopUp={!topupsDisableReason}
          solution={plansAndSolution.solution}
        />
      )}

      {planId && selectedPlan && (
        <ChangeLensReviewView
          nextPack={nextPack}
          nextUnpaidPack={nextUnpaidPack}
          selectedPlan={selectedPlan}
          onCancel={onCancel}
          onConfirm={onConfirmChange}
          solutionPrice={
            withSolution && plansAndSolution.solution && plansAndSolution.solution.price
          }
        />
      )}
    </PageContainer>
  );
};

ChangeLensAmountContainer.displayName = 'ChangeLensAmountContainer';

ChangeLensAmountContainer.propTypes = {
  getProductOptionsFunc: PropTypes.func.isRequired,
};

export default ChangeLensAmountContainer;
